import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", business_name: "", email: "", phone: "", password: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register({
        name: form.name.trim(),
        business_name: form.business_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        password: form.password,
      });
      toast.success("Account created — welcome!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6">
      <div className="w-full max-w-md animate-fade-up">
        <div className="flex items-center gap-3 mb-8">
          <img src="/pitcrew-logo-horizontal.png" alt="Pitcrew" className="h-9 object-contain" data-testid="register-logo" />
        </div>
        <h1 className="font-head font-bold text-2xl text-slate-50">Create your shop account</h1>
        <p className="text-slate-500 text-sm mt-1 mb-7">Start invoicing in minutes. Free trial included.</p>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-slate-300">Your name</Label>
              <Input required value={form.name} onChange={set("name")} data-testid="register-name-input"
                placeholder="Alex Rivera" className="h-11 bg-slate-950 border-slate-700 focus-visible:ring-amber-500/50" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300">Phone</Label>
              <Input value={form.phone} onChange={set("phone")} data-testid="register-phone-input"
                placeholder="555-0100" className="h-11 bg-slate-950 border-slate-700 focus-visible:ring-amber-500/50" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300">Shop / business name</Label>
            <Input value={form.business_name} onChange={set("business_name")} data-testid="register-business-input"
              placeholder="Rivera Auto Repair" className="h-11 bg-slate-950 border-slate-700 focus-visible:ring-amber-500/50" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300">Email</Label>
            <Input type="email" required value={form.email} onChange={set("email")} data-testid="register-email-input"
              placeholder="you@shop.com" className="h-11 bg-slate-950 border-slate-700 focus-visible:ring-amber-500/50" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300">Password</Label>
            <Input type="password" required value={form.password} onChange={set("password")} data-testid="register-password-input"
              placeholder="Create a password" className="h-11 bg-slate-950 border-slate-700 focus-visible:ring-amber-500/50" />
          </div>
          <Button type="submit" disabled={busy} data-testid="register-submit-button"
            className="w-full h-11 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Create account
          </Button>
        </form>

        <p className="text-sm text-slate-500 text-center mt-7">
          Already have an account?{" "}
          <Link to="/login" data-testid="go-to-login" className="text-amber-400 hover:text-amber-300 font-medium">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
